import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SellerUniversityHeaderLayout } from '@amzn/seller-university-ui-components';
import { KatButton, KatIcon } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { ROUTES } from 'src/constants/route';
import { BUNDLE_NAME, NAVIGATION_STRING_ID, CORE_STRING_ID } from 'src/constants/localization';
import { getAppRoute } from 'src/utils/routing';
import { useClientId, useMarketplaceCountryCode, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { getNavRefTag } from 'src/constants/refTag';
import Logger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { AutoCompleteSearchWrapperV2 } from '../autocomplete';
import TopicsDropdownButton from '../button/TopicsDropdownButton';

const Header = () => {
  const [navigationBundle] = useBundle(BUNDLE_NAME.NAVIGATION);
  const [coreBundle] = useBundle(BUNDLE_NAME.CORE);
  const navigate = useNavigate();
  const clientId = useClientId();
  const marketplaceCountryCode = useMarketplaceCountryCode();
  const { clickMetric } = useMetrics();
  const searchRef = React.useRef<{ triggerSearch:() => void }>(null);

  // Logo/Home Component
  const homeLogo = (
    <Link
      to={getAppRoute(ROUTES.LEARN.HOME)}
      className="!no-underline hover:!no-underline focus:!no-underline"
      onClick={() => clickMetric({ feature: FeatureName.NavHomeLogoClick })}
    >
      <div className="text-su-heading-600 font-bold text-su-text-primary">
        {t(navigationBundle, NAVIGATION_STRING_ID.SU_LABEL)}
      </div>
    </Link>
  );

  // Search Bar Component with AutoComplete
  const searchBar = <AutoCompleteSearchWrapperV2 ref={searchRef} />;

  // Search Button Component
  const searchButton = (
    <KatButton variant="secondary" onClick={() => searchRef.current?.triggerSearch()}>
      {t(coreBundle, CORE_STRING_ID.ACTION_SEARCH)}
    </KatButton>
  );

  // Webinar Link Button
  const webinarLinkButton = (
    <KatButton
      variant="link"
      className="[--background-active:transparent] [--background-hover:transparent] [--border-color-active:transparent]
      [--border-color-hover:transparent] [--box-shadow-hover:none] [--box-shadow:none] [--color-active:inherit]
      [--color-hover:inherit] [--color:inherit] [--focus-ring-width:0]"
      onClick={() => {
        clickMetric({ feature: FeatureName.NavLiveEventsClick });
        if (clientId === UNGATED_CLIENT_ID) {
          // Ungated user: redirect to Seller Central welcome page with a ref tag.
          // Uses window.location.origin to handle all regional SC domains (co.jp, de, etc.).
          if (!marketplaceCountryCode) {
            Logger.warn(ClientLog.IllegalInternalDataState, new Error('marketplaceCountryCode unavailable for nav ref tag'), {
              message: 'Redirecting to SC welcome page without ref tag',
            });
          }
          const refTag = getNavRefTag(marketplaceCountryCode);
          window.location.href = refTag ? `/?ref_=${refTag}` : '/';
        } else {
          navigate(ROUTES.LEARN.WEBINAR);
        }
      }}
    >
      <div className="flex items-center gap-su-space-03">
        <KatIcon name="videocam" size="medium" />
        {t(navigationBundle, NAVIGATION_STRING_ID.WEBINAR)}
      </div>
    </KatButton>
  );

  return (
    <SellerUniversityHeaderLayout
      homeLogo={homeLogo}
      searchBar={searchBar}
      searchButton={searchButton}
      webinarLinkButton={webinarLinkButton}
      topicsLinkButton={<TopicsDropdownButton />}
    />
  );
};

export default Header;
